import { Suspense, useDeferredValue, useState } from "react";
import { useSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { relocationFlowReportQuery } from "../GraphQL/relocationFlowReportQuery";
import { RelocationFlowMap } from "../Components/RelocationFlowMap";
import {
  RelocationFlowMatrix,
  useMatrixExport,
  type HeatMetric,
} from "../Components/RelocationFlowMatrix";
import {
  RelocationFlowControls,
  type TimeRange,
} from "../Components/RelocationFlowControls";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/catalyst/button";
import { Switch } from "@/components/catalyst/switch";
import { DownloadIcon } from "lucide-react";
import { useSearch, useNavigate } from "@tanstack/react-router";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { useTranslations } from "use-intl";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";

export function ReportRelocationFlowScreen() {
  const { year, month } = useSearch({
    from: "/$tenant/$orgId/supplier/{-$supplierId}/reports/relocation-flow",
  });
  const navigate = useNavigate({
    from: "/$tenant/$orgId/supplier/{-$supplierId}/reports/relocation-flow",
  });
  const [showDepartures, setShowDepartures] = useState(true);
  const [showDeliveries, setShowDeliveries] = useState(false);
  const [showRoutes, setShowRoutes] = useState(false);
  const [timeRange, setTimeRange] = useState<TimeRange>(
    month === null ? "yearly" : "monthly",
  );
  const t = useTranslations("report");

  const deferredYear = useDeferredValue(year);
  const deferredMonth = useDeferredValue(month);
  const isPending = deferredYear !== year || deferredMonth !== month;

  function handleDateChange(newYear: number, newMonth: number | null) {
    navigate({
      params: (prev) => prev,
      search: { year: newYear, month: newMonth },
    });
  }

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                from="/$tenant/$orgId/supplier/{-$supplierId}"
                to="/$tenant/$orgId/supplier/{-$supplierId}/reports"
                params={(prev) => prev}
              >
                {t("reporting")}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t("relocationFlow")}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center justify-between gap-4">
            <span>{t("relocationFlowReport")}</span>
            <Suspense fallback={null}>
              <FlowReportHeaderActions
                year={deferredYear}
                month={deferredMonth}
              />
            </Suspense>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <RelocationFlowControls
            year={year}
            month={month}
            timeRange={timeRange}
            showDepartures={showDepartures}
            showDeliveries={showDeliveries}
            showRoutes={showRoutes}
            onDateChange={handleDateChange}
            onTimeRangeChange={setTimeRange}
            onShowDeparturesChange={setShowDepartures}
            onShowDeliveriesChange={setShowDeliveries}
            onShowRoutesChange={setShowRoutes}
          />

          <Suspense fallback={<Skeleton className="h-[500px] w-full" />}>
            <RelocationFlowContent
              year={deferredYear}
              month={deferredMonth}
              showDepartures={showDepartures}
              showDeliveries={showDeliveries}
              showRoutes={showRoutes}
              isPending={isPending}
            />
          </Suspense>
        </CardContent>
      </Card>
    </div>
  );
}

function RelocationFlowContent({
  year,
  month,
  showDepartures,
  showDeliveries,
  showRoutes,
  isPending,
}: {
  year: number;
  month: number | null;
  showDepartures: boolean;
  showDeliveries: boolean;
  showRoutes: boolean;
  isPending: boolean;
}) {
  const t = useTranslations("report");
  const [heatMetric, setHeatMetric] = useState<HeatMetric>("bookings");
  const { supplierId } = useGlobalSupplier();
  const { data } = useSuspenseGqlQuery(relocationFlowReportQuery, {
    year,
    month,
    supplierId: supplierId ?? undefined,
  });

  const report = data.relocationFlowReport;

  return (
    <div
      className="space-y-6 border-t pt-4 transition-opacity duration-200"
      style={{ opacity: isPending ? 0.5 : 1 }}
    >
      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-gray-900">
          {t("cityFlowMap")}
        </h3>
        <RelocationFlowMap
          cities={report.cities}
          movements={report.movements}
          showDepartures={showDepartures}
          showDeliveries={showDeliveries}
          showRoutes={showRoutes}
        />
      </section>

      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-900">
            {t("movementMatrix")}
          </h3>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-500">{t("colorBy")}</span>
            <span
              className={
                heatMetric === "bookings"
                  ? "font-medium text-gray-900"
                  : "text-gray-400"
              }
            >
              {t("heatBookings")}
            </span>
            <Switch
              checked={heatMetric === "time"}
              onChange={(checked) =>
                setHeatMetric(checked ? "time" : "bookings")
              }
            />
            <span
              className={
                heatMetric === "time"
                  ? "font-medium text-gray-900"
                  : "text-gray-400"
              }
            >
              {t("heatTimeToBook")}
            </span>
          </div>
        </div>
        <RelocationFlowMatrix
          cities={report.cities}
          movements={report.movements}
          heatMetric={heatMetric}
        />
      </section>
    </div>
  );
}

function FlowReportHeaderActions({
  year,
  month,
}: {
  year: number;
  month: number | null;
}) {
  const t = useTranslations("report");
  const { supplierId } = useGlobalSupplier();
  const { data } = useSuspenseGqlQuery(relocationFlowReportQuery, {
    year,
    month,
    supplierId: supplierId ?? undefined,
  });

  const report = data.relocationFlowReport;
  const filename = `relocation-flow-${year}${month ? `-${String(month).padStart(2, "0")}` : ""}`;
  const exportCsv = useMatrixExport(report.cities, report.movements, filename);

  return (
    <div className="flex items-center gap-4">
      <span className="text-sm font-normal text-gray-500">
        {t("completedBookings", { count: report.total_bookings })}
      </span>
      <Button outline onClick={exportCsv}>
        <DownloadIcon className="mr-2 size-4" />
        {t("exportCsv")}
      </Button>
    </div>
  );
}
